/**
 * Eksport i backup (Faza 8, PLAN §11).
 *
 * "Pobierz eksport" to zwykła nawigacja do `/api/export` - przeglądarka
 * zapisuje plik sama, z nazwą z Content-Disposition. Poniżej stan nocnego
 * backupu (katalog, retencja, ostatnia kopia) i przycisk "zrób kopię teraz".
 */

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { EXPORT_DOWNLOAD, api } from "../api";
import { settingsKeys } from "./SettingsPage";

function formatMoment(value: string | null | undefined): string {
  if (!value) return "nigdy";
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? String(value) : at.toLocaleString("pl-PL");
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

export function ExportSection() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: settingsKeys.backups,
    queryFn: api.backups,
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  async function runBackup() {
    setBusy(true);
    setResult(null);
    try {
      const made = await api.runBackup();
      const pruned = made.pruned.length ? `, skasowano starych: ${made.pruned.length}` : "";
      setResult(`Zapisano ${made.file} (${formatSize(made.size)})${pruned}.`);
      void qc.invalidateQueries({ queryKey: settingsKeys.backups });
    } catch (exc) {
      setResult(exc instanceof Error ? exc.message : String(exc));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card settings__section">
      <h2 className="card__title">Eksport i backup</h2>
      <p className="muted">
        Zip z markdownami (inbox, projekty, listy, checklisty, pamięć) i pełnym{" "}
        <code>full-dump.json</code>. Tokeny Google i subskrypcje push zostają poza eksportem.
      </p>

      <div className="clarify__buttons">
        <a className="btn btn--primary" href={EXPORT_DOWNLOAD} download>
          Pobierz eksport
        </a>
        <button className="btn" disabled={busy} onClick={() => void runBackup()}>
          {busy ? "Robię kopię…" : "Zrób kopię teraz"}
        </button>
      </div>
      {result && <p className="muted">{result}</p>}

      {isLoading && <p className="muted">Ładowanie…</p>}
      {error && <p className="error">Nie udało się pobrać stanu backupów.</p>}

      {data && (
        <ul className="rows">
          <li className="row">
            <div className="row__main">
              <span className="row__title">Nocny backup</span>
              <span className="row__meta">
                {data.enabled
                  ? `codziennie ${pad(data.hour)}:${pad(data.minute)}, retencja ${data.retention_days} dni`
                  : "wyłączony (BACKUP_ENABLED=0)"}
              </span>
            </div>
          </li>
          <li className="row">
            <div className="row__main">
              <span className="row__title">Katalog</span>
              <span className="row__meta">
                <code>{data.directory}</code>
                {!data.exists && " — jeszcze nie istnieje"}
              </span>
            </div>
          </li>
          <li className="row">
            <div className="row__main">
              <span className="row__title">Ostatnia kopia</span>
              <span className="row__meta">
                {data.last
                  ? `${formatMoment(data.last.created_at)} · ${formatSize(data.last.size)}`
                  : "nigdy"}
              </span>
            </div>
          </li>
          <li className="row">
            <div className="row__main">
              <span className="row__title">Kopie na dysku</span>
              <span className="row__meta">
                {data.count} · łącznie {formatSize(data.total_size)}
              </span>
            </div>
          </li>
        </ul>
      )}
    </section>
  );
}
